import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Heart } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { COMPANY, TIMELINE, LEADERSHIP, WAREHOUSE_IMAGE, FACTORY_IMAGE } from "@/lib/site-data";

export default function About() {
    return (
        <div data-testid="page-about">
            <PageHeader
                overline="About Vibhuti Logistics"
                title="20 years of building India's industrial supply chains."
                subtitle="From a single-truck operation in Aurangabad in 2004 to an integrated 3PL & 4PL partner for India's automotive and engineering manufacturers."
                image={FACTORY_IMAGE}
                breadcrumb={[{ label: "About" }]}
                testId="about-header"
            />

            {/* Intro */}
            <section className="bg-white py-20" data-testid="about-intro">
                <div className="container-vl grid grid-cols-12 gap-10 items-start">
                    <div className="col-span-12 lg:col-span-5">
                        <p className="overline mb-4">Who we are</p>
                        <h2 className="heading-h2 text-[#101A2C]">A logistics partner — not a transporter.</h2>
                    </div>
                    <div className="col-span-12 lg:col-span-7 space-y-5 body-lg">
                        <p>
                            Vibhuti Logistics was founded in 2004 in Chhatrapati Sambhajinagar (Aurangabad) with a simple commitment — to give manufacturers a logistics partner who behaves like an extension of their plant.
                        </p>
                        <p>
                            Over two decades, that commitment has scaled into a 100+ vehicle operational network, owned warehouses at Waluj and Chakan, and trusted relationships with 45+ industrial clients across automotive, auto-ancillary, engineering and project industries.
                        </p>
                        <p>
                            Today, we deliver integrated transportation, warehousing, contract and reverse logistics — backed by GPS tracking, WMS, MIS reporting and a 40+ member operations team obsessed with on-time SLAs.
                        </p>
                    </div>
                </div>
            </section>

            {/* Vision / Mission / Values */}
            <section className="bg-[#F8FAFC] py-20 border-y border-[#CBD5E1]" data-testid="about-vmv">
                <div className="container-vl">
                    <p className="overline mb-4 text-center">Vision · Mission · Values</p>
                    <h2 className="heading-h2 text-[#101A2C] text-center mb-14 max-w-3xl mx-auto">
                        Vision 2030: To be the foremost leader in logistics excellence.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#CBD5E1] border border-[#CBD5E1]">
                        {[
                            { icon: Eye, t: "Vision", body: "To be the foremost leader in logistics excellence in western India — recognized for technology, reliability and integrated supply-chain capability." },
                            { icon: Target, t: "Mission", body: "Provide unparalleled logistical solutions through deep industry expertise, owned infrastructure and operational excellence — every single shipment." },
                            { icon: Heart, t: "Values", body: "Integrity in commitments. Excellence in execution. Collaboration with customers, partners and our own team." },
                        ].map((v) => (
                            <div key={v.t} className="bg-white p-8" data-testid={`vmv-${v.t.toLowerCase()}`}>
                                <v.icon className="h-9 w-9 text-[#F4A000] mb-5" strokeWidth={1.5} />
                                <h3 className="font-display uppercase text-2xl text-[#101A2C] mb-3">{v.t}</h3>
                                <p className="text-sm text-[#4B6390] leading-relaxed">{v.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline */}
            <section className="bg-white py-20 md:py-28" data-testid="about-timeline">
                <div className="container-vl">
                    <p className="overline mb-4">Our Journey</p>
                    <h2 className="heading-h2 text-[#101A2C] mb-14 max-w-3xl">2004 → Present.<br />Two decades. Every step on the corridor.</h2>
                    <div className="relative pl-6 md:pl-10">
                        <div className="absolute left-0 top-2 bottom-2 w-px bg-[#CBD5E1]" />
                        <div className="space-y-10">
                            {TIMELINE.map((t, i) => (
                                <div key={t.year} className="relative" data-testid={`timeline-${t.year}`}>
                                    <span className="absolute -left-6 md:-left-10 top-1 w-3 h-3 bg-[#F4A000] border-2 border-[#101A2C]" />
                                    <div className="grid grid-cols-12 gap-4">
                                        <div className="col-span-12 md:col-span-2">
                                            <p className="font-display text-3xl md:text-4xl text-[#28406A]">{t.year}</p>
                                        </div>
                                        <div className="col-span-12 md:col-span-10">
                                            <h3 className="font-display uppercase text-xl text-[#101A2C] mb-1">{t.title}</h3>
                                            <p className="text-sm md:text-base text-[#4B6390] leading-relaxed max-w-2xl">{t.body}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Leadership */}
            <section className="bg-[#101A2C] py-20 md:py-28 grain-overlay" data-testid="about-leadership">
                <div className="container-vl">
                    <p className="overline mb-4">Leadership</p>
                    <h2 className="heading-h2 text-white mb-14 max-w-3xl">The team behind every on-time delivery.</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {LEADERSHIP.map((l, i) => (
                            <div key={l.name} className="bg-[#28406A] p-8 border-l-4 border-[#F4A000]" data-testid={`leader-${i}`}>
                                <div className="w-16 h-16 bg-[#F4A000] text-[#101A2C] font-display text-2xl flex items-center justify-center mb-6">
                                    {l.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                                </div>
                                <h3 className="font-display uppercase text-xl text-white mb-1">{l.name}</h3>
                                <p className="text-xs uppercase tracking-[0.2em] text-[#F4A000] mb-4">{l.role}</p>
                                <p className="text-sm text-[#CBD5E1] leading-relaxed">{l.bio}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#F4A000] py-16" data-testid="about-cta">
                <div className="container-vl flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <h3 className="heading-h3 text-[#101A2C] max-w-2xl">Want to see how Vibhuti can fit your supply chain?</h3>
                    <Link to="/contact" data-testid="about-cta-btn" className="btn-navy">
                        Talk to an Expert <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
